import { 
  doc, 
  collection, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  onSnapshot,
  arrayUnion,
  setDoc,
  writeBatch
} from '../firebase.config';
import { 
  createUserWithEmailAndPassword, 
  sendEmailVerification,
  signOut,
  updateProfile
} from 'firebase/auth';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage, auth, handleFirebaseError } from '../firebase.config';

// 🔐 Enhanced MPIN Management Utilities
export class MPINManager {
  // ⚠️ SECURITY WARNING: This decodes Base64-encoded MPINs
  // Base64 is NOT encryption - it's easily reversible encoding
  // This should only be used for recovery/debugging purposes
  // In production, replace btoa() with proper hashing (bcrypt, argon2, etc.)
  static decodeMPIN(encodedMPIN) {
    try {
      if (!encodedMPIN) {
        return { success: false, error: 'No encoded MPIN provided' };
      }
      const decoded = atob(encodedMPIN);
      return { success: true, mpin: decoded };
    } catch (error) {
      return { success: false, error: `Failed to decode MPIN: ${error.message}` };
    }
  }

  // Get and decode user MPIN (for recovery/debugging only)
  static async getUserMPIN(userId) {
    try {
      const userRef = doc(db, `user_settings/${userId}`);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        return { success: false, error: 'User settings not found' };
      }
      
      const userData = userSnap.data();
      const encodedMPIN = userData.mpin;
      
      if (!encodedMPIN) {
        return { success: false, error: 'No MPIN set for user' };
      }
      
      return this.decodeMPIN(encodedMPIN);
    } catch (error) {
      handleFirebaseError(error, 'User MPIN retrieval');
      return { success: false, error: error.message };
    }
  }

  // Get and decode company MPIN (for recovery/debugging only)
  static async getCompanyMPIN(companyId) {
    try {
      // Check security subcollection first (newer storage location)
      const securityRef = doc(db, `companies/${companyId}/security/company_mpin`);
      const securitySnap = await getDoc(securityRef);
      
      if (securitySnap.exists()) {
        const securityData = securitySnap.data();
        const encodedMPIN = securityData.hash;
        
        if (encodedMPIN) {
          return this.decodeMPIN(encodedMPIN);
        }
      }
      
      // Fallback to company root document (legacy storage location)
      const companyRef = doc(db, `companies/${companyId}`);
      const companySnap = await getDoc(companyRef);
      
      if (!companySnap.exists()) {
        return { success: false, error: 'Company not found' };
      }
      
      const companyData = companySnap.data();
      const encodedMPIN = companyData.company_mpin;
      
      if (!encodedMPIN) {
        return { success: false, error: 'No company MPIN set' };
      }
      
      return this.decodeMPIN(encodedMPIN);
    } catch (error) {
      handleFirebaseError(error, 'Company MPIN retrieval');
      return { success: false, error: error.message };
    }
  }

  // User MPIN verification
  static async verifyUserMPIN(userId, mpin) {
    try {
      // For demo purposes, we'll use a simple hash comparison
      // In production, use proper encryption/hashing
      const hashedMPIN = btoa(mpin); // Base64 encoding (replace with proper hashing)
      
      // Get user MPIN from settings
      const userRef = doc(db, `user_settings/${userId}`);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        return false;
      }
      
      const userData = userSnap.data();
      return userData.mpin === hashedMPIN;
    } catch (error) {
      handleFirebaseError(error, 'User MPIN verification');
      return false;
    }
  }

  // Company MPIN verification
  static async verifyCompanyMPIN(companyId, mpin) {
    try {
      const hashedMPIN = btoa(mpin); // Base64 encoding (replace with proper hashing)
      
      // Get company MPIN from company data
      const companyRef = doc(db, `companies/${companyId}`);
      const companySnap = await getDoc(companyRef);
      
      if (!companySnap.exists()) {
        return false;
      }
      
      const companyData = companySnap.data();
      return companyData.company_mpin === hashedMPIN;
    } catch (error) {
      handleFirebaseError(error, 'Company MPIN verification');
      return false;
    }
  }
  
  // Set user MPIN
  static async setUserMPIN(userId, mpin) {
    try {
      const hashedMPIN = btoa(mpin); // Base64 encoding (replace with proper hashing)
      
      const userRef = doc(db, `user_settings/${userId}`);
      await updateDoc(userRef, {
        mpin: hashedMPIN,
        mpin_set_at: serverTimestamp(),
        mpin_updated_at: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'User MPIN setup');
      return { success: false, error: error.message };
    }
  }

  // Set company MPIN
  static async setCompanyMPIN(companyId, mpin, setByUserId) {
    try {
      const hashedMPIN = btoa(mpin); // Base64 encoding (replace with proper hashing)
      
      const companyRef = doc(db, `companies/${companyId}`);
      await updateDoc(companyRef, {
        company_mpin: hashedMPIN,
        company_mpin_set_at: serverTimestamp(),
        company_mpin_set_by: setByUserId,
        company_mpin_updated_at: serverTimestamp()
      });
      
      // Log company MPIN setup
      await this.logMPINAttempt(setByUserId, companyId, true, 'Company MPIN set');
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'Company MPIN setup');
      return { success: false, error: error.message };
    }
  }

  // Get MPIN policy for company
  static async getCompanyMPINPolicy(companyId) {
    try {
      const companyRef = doc(db, `companies/${companyId}`);
      const companySnap = await getDoc(companyRef);
      
      if (!companySnap.exists()) {
        return {
          requireUserMPIN: true,
          requireCompanyMPIN: false,
          mpinTimeout: 300000, // 5 minutes
          attemptLimit: 3,
          criticalOperations: ['delete', 'edit'],
          criticalPages: ['payments', 'employee_management']
        };
      }
      
      const companyData = companySnap.data();
      return companyData.mpin_policy || {
        requireUserMPIN: true,
        requireCompanyMPIN: false,
        mpinTimeout: 300000,
        attemptLimit: 3,
        criticalOperations: ['delete', 'edit'],
        criticalPages: ['payments', 'employee_management']
      };
    } catch (error) {
      handleFirebaseError(error, 'MPIN policy retrieval');
      return {
        requireUserMPIN: true,
        requireCompanyMPIN: false,
        mpinTimeout: 300000,
        attemptLimit: 3,
        criticalOperations: ['delete', 'edit'],
        criticalPages: ['payments', 'employee_management']
      };
    }
  }

  // Determine MPIN requirement for operation
  static async checkMPINRequirement(companyId, userId, page, operation) {
    try {
      // Get company MPIN policy
      const policy = await this.getCompanyMPINPolicy(companyId);
      
      // Check if operation is in critical list
      const isCriticalOperation = policy.criticalOperations.includes(operation);
      const isCriticalPage = policy.criticalPages.includes(page);
      
      // Get employee-specific requirements
      const employee = await EmployeeManager.getEmployeeByIdentifier(companyId, userId, 'userId');
      let hasEmployeeRequirement = false;
      
      if (employee.success) {
        const pageOperation = `${page}_${operation}`;
        hasEmployeeRequirement = employee.data.mpinRequired?.includes(pageOperation) || false;
      }
      
      // Determine requirement
      if (hasEmployeeRequirement) {
        return {
          required: true,
          type: 'user',
          reason: 'Employee-specific MPIN requirement',
          policy: policy
        };
      }
      
      if (policy.requireCompanyMPIN && (isCriticalOperation || isCriticalPage)) {
        return {
          required: true,
          type: 'company',
          reason: 'Company policy requirement for critical operations',
          policy: policy
        };
      }
      
      if (policy.requireUserMPIN && (isCriticalOperation || isCriticalPage)) {
        return {
          required: true,
          type: 'user',
          reason: 'Company policy requirement for critical operations',
          policy: policy
        };
      }
      
      return {
        required: false,
        type: null,
        reason: 'No MPIN requirement',
        policy: policy
      };
    } catch (error) {
      handleFirebaseError(error, 'MPIN requirement check');
      return {
        required: false,
        type: null,
        reason: 'Error checking requirement',
        policy: null
      };
    }
  }
  
  // Enhanced MPIN attempt logging
  static async logMPINAttempt(userId, companyId, success, notes = '', mpinType = 'user') {
    try {
      await addDoc(collection(db, `companies/${companyId}/mpin_logs`), {
        userId,
        companyId,
        success,
        notes,
        mpinType,
        timestamp: serverTimestamp(),
        ip_address: 'unknown', // Can be enhanced with actual IP detection
        user_agent: navigator.userAgent,
        session_id: `${userId}_${Date.now()}`
      });
    } catch (error) {
      handleFirebaseError(error, 'MPIN attempt logging');
    }
  }
}

// 👥 Enhanced Employee Management Utilities
export class EmployeeManager {
  /** Digits-only phone for comparison (handles +91 / leading 0). */
  static normalizePhoneDigits(raw) {
    let d = String(raw ?? '').replace(/\D/g, '');
    if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
    if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
    return d;
  }

  /**
   * Statuses hidden by default in Employee Management (inactive / deleted / removed).
   * Another employee in one of these states does not "claim" the phone for new hires.
   */
  static employeeAllowsPhoneReuse(status) {
    const s = String(status || 'active').toLowerCase();
    return s === 'deleted' || s === 'inactive' || s === 'removed';
  }

  /**
   * If another employee still "active" for business purposes uses this phone, return that row.
   * Hidden (deleted/inactive/removed) records do not block adding a new employee with the same number.
   */
  static async findBlockingPhoneConflict(companyId, phone, excludeDocId = null) {
    const res = await this.getCompanyEmployees(companyId, { status: 'all' });
    if (!res.success || !res.data?.length) return null;
    const target = EmployeeManager.normalizePhoneDigits(phone);
    if (target.length < 10) return null;
    for (const emp of res.data) {
      if (excludeDocId && emp.id === excludeDocId) continue;
      const empPhone = EmployeeManager.normalizePhoneDigits(emp.phone || emp.contact || '');
      if (empPhone !== target) continue;
      if (!EmployeeManager.employeeAllowsPhoneReuse(emp.status)) {
        return emp;
      }
    }
    return null;
  }

  // Generate unique employee ID
  static async generateEmployeeId(companyId) {
    try {
      // Include all statuses (including deleted/inactive) so EMP numbers never reuse and stay unique
      const existingEmployees = await this.getCompanyEmployees(companyId, { status: 'all' });
      const employees = existingEmployees.success ? existingEmployees.data : [];
      
      // Find max employee ID
      const maxId = employees.reduce((max, emp) => {
        const match = (emp.employeeId || '').match(/^EMP(\d+)$/);
        return match ? Math.max(max, parseInt(match[1], 10)) : max;
      }, 0);
      
      return `EMP${String(maxId + 1).padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating employee ID:', error);
      // Fallback to timestamp-based ID
      return `EMP${String(Date.now()).slice(-3)}`;
    }
  }

  // Generate global employee ID for multi-company tracking
  static async generateGlobalEmployeeId() {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `GLOBAL_EMP_${timestamp}_${random}`;
  }

  // Create employee with enhanced validation
  static async createEmployee(companyId, employeeData) {
    try {
      // Never persist Firestore document id as a field — it caused duplicate keys / wrong merges
      const { id: _omitStoredId, ...employeeDataClean } = employeeData || {};

      // Required fields only (department is optional — UI no longer collects it)
      const name = String(employeeDataClean.name ?? '').trim();
      const phone = String(employeeDataClean.phone ?? '').trim();
      const designation = String(employeeDataClean.designation ?? '').trim();
      const role = String(employeeDataClean.role ?? '').trim();
      if (!name) return { success: false, error: 'name is required' };
      if (!phone) return { success: false, error: 'phone is required' };
      if (!designation) return { success: false, error: 'designation is required' };
      if (!role) return { success: false, error: 'role is required' };

      const phoneConflict = await this.findBlockingPhoneConflict(companyId, phone, null);
      if (phoneConflict) {
        return {
          success: false,
          error:
            'This phone number is already used by an active employee. Change the number or set the other record to inactive/deleted first.'
        };
      }

      // Generate IDs if not provided
      const enhancedEmployeeData = {
        ...employeeDataClean,
        // Optional legacy field; keep empty when not provided (never block create)
        department:
          employeeDataClean.department != null && String(employeeDataClean.department).trim() !== ''
            ? String(employeeDataClean.department).trim()
            : '',
        employeeId: employeeDataClean.employeeId || (await this.generateEmployeeId(companyId)),
        globalEmployeeId: employeeDataClean.globalEmployeeId || (await this.generateGlobalEmployeeId()),
        companyId: companyId,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        status: employeeDataClean.status || 'active',
        
        // Default permissions structure
        permissions: employeeDataClean.permissions || {},
        assignedPages: employeeDataClean.assignedPages || [],
        mpinRequired: employeeDataClean.mpinRequired || [],
        
        // Cash balance tracking (initialize to zero)
        cashBalance: employeeDataClean.cashBalance || {
          total: 0,
          cash: 0,
          upi: 0,
          lastUpdated: serverTimestamp()
        },
        
        // Payment preferences
        paymentPreferences: employeeDataClean.paymentPreferences || {
          defaultMode: employeeDataClean.paymentPreferences?.defaultMode || 'cash',
          upiId: employeeDataClean.paymentPreferences?.upiId || '',
          upiApp: employeeDataClean.paymentPreferences?.upiApp || '',
          cashLimit: employeeDataClean.paymentPreferences?.cashLimit || 0,
          upiLimit: employeeDataClean.paymentPreferences?.upiLimit || 0
        },
        
        // Salary structure (basic + bonus annual; legacy component fields zeroed)
        salary_info: {
          basic: parseFloat(employeeDataClean.basicSalary) || 0,
          bonus: parseFloat(employeeDataClean.bonus) || 0,
          hra: 0,
          conveyance: 0,
          pf: 0,
          gratuity: 0,
          special: 0
        },
        
        // Calculated amounts for quick access
        ctc:
          parseFloat(employeeDataClean.ctc) ||
          (parseFloat(employeeDataClean.basicSalary) || 0) + (parseFloat(employeeDataClean.bonus) || 0),
        basicSalaryAmount: parseFloat(employeeDataClean.basicSalary) || 0,
        bonusAmount: parseFloat(employeeDataClean.bonus) || 0,
        hraAmount: 0,
        conveyanceAmount: 0,
        employerPFAmount: 0,
        gratuityAmount: 0,
        specialAllowanceAmount: 0
      };

      const employeeRef = await addDoc(collection(db, `companies/${companyId}/employees`), enhancedEmployeeData);
      
      // Log employee creation
      await this.logEmployeeActivity(companyId, employeeRef.id, 'created', 'Employee created successfully');
      
      return { success: true, employeeId: employeeRef.id, data: enhancedEmployeeData };
    } catch (error) {
      handleFirebaseError(error, 'Employee creation');
      return { success: false, error: error.message };
    }
  }
  
  // Update employee with permission validation
  static async updateEmployee(companyId, employeeId, updateData) {
    try {
      const { id: _omitStoredId, ...cleanUpdate } = updateData || {};

      const employeeRef = doc(db, `companies/${companyId}/employees/${employeeId}`);
      
      // Get current employee data
      const currentEmployee = await getDoc(employeeRef);
      if (!currentEmployee.exists()) {
        return { success: false, error: 'Employee not found' };
      }

      const currentData = currentEmployee.data();

      if (cleanUpdate.phone !== undefined || cleanUpdate.contact !== undefined) {
        const effectivePhone =
          cleanUpdate.phone !== undefined
            ? cleanUpdate.phone
            : cleanUpdate.contact !== undefined
              ? cleanUpdate.contact
              : currentData.phone || currentData.contact || '';
        const phoneConflict = await this.findBlockingPhoneConflict(companyId, effectivePhone, employeeId);
        if (phoneConflict) {
          return {
            success: false,
            error:
              'This phone number is already used by another active employee. Change the number or set the other record to inactive/deleted first.'
          };
        }
      }
      
      const enhancedUpdateData = {
        ...cleanUpdate,
        updated_at: serverTimestamp(),
        
        // Preserve or initialize cash balance if not provided
        cashBalance: cleanUpdate.cashBalance || currentData.cashBalance || {
          total: 0,
          cash: 0,
          upi: 0,
          lastUpdated: serverTimestamp()
        },
        
        // Preserve or initialize payment preferences if not provided
        paymentPreferences: cleanUpdate.paymentPreferences || currentData.paymentPreferences || {
          defaultMode: 'cash',
          upiId: '',
          upiApp: '',
          cashLimit: 0,
          upiLimit: 0
        },
        
        // Simplified salary: basic + bonus (annual); clear legacy component amounts when salary fields sent
        ...(cleanUpdate.basicSalary !== undefined || cleanUpdate.bonus !== undefined
          ? (() => {
              const basic = parseFloat(cleanUpdate.basicSalary);
              const bonus = parseFloat(cleanUpdate.bonus);
              const basicAmt = Number.isFinite(basic) ? basic : 0;
              const bonusAmt = Number.isFinite(bonus) ? bonus : 0;
              return {
                basicSalaryAmount: basicAmt,
                bonusAmount: bonusAmt,
                hraAmount: 0,
                conveyanceAmount: 0,
                employerPFAmount: 0,
                gratuityAmount: 0,
                specialAllowanceAmount: 0,
                ctc: basicAmt + bonusAmt,
                salary_info: {
                  basic: basicAmt,
                  bonus: bonusAmt,
                  hra: 0,
                  conveyance: 0,
                  pf: 0,
                  gratuity: 0,
                  special: 0
                }
              };
            })()
          : {})
      };

      await updateDoc(employeeRef, enhancedUpdateData);
      
      // Log employee update
      await this.logEmployeeActivity(companyId, employeeId, 'updated', 'Employee updated successfully');
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'Employee update');
      return { success: false, error: error.message };
    }
  }

  // Delete employee (soft delete)
  static async deleteEmployee(companyId, employeeId) {
    try {
      const employeeRef = doc(db, `companies/${companyId}/employees/${employeeId}`);
      await updateDoc(employeeRef, {
        status: 'deleted',
        deleted_at: serverTimestamp(),
        updated_at: serverTimestamp()
      });
      
      // Log employee deletion
      await this.logEmployeeActivity(companyId, employeeId, 'deleted', 'Employee deleted');
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'Employee deletion');
      return { success: false, error: error.message };
    }
  }
  
  static async getEmployee(companyId, employeeId) {
    try {
      const employeeRef = doc(db, `companies/${companyId}/employees/${employeeId}`);
      const employeeSnap = await getDoc(employeeRef);
      
      if (employeeSnap.exists()) {
        return { success: true, data: { id: employeeSnap.id, ...employeeSnap.data() } };
      } else {
        return { success: false, error: 'Employee not found' };
      }
    } catch (error) {
      handleFirebaseError(error, 'Employee retrieval');
      return { success: false, error: error.message };
    }
  }
  
  // Get company employees with filtering
  static async getCompanyEmployees(companyId, filters = {}) {
    try {
      let employeesQuery = collection(db, `companies/${companyId}/employees`);
      
      // Apply filters
      const constraints = [];
      
      // Status filter (default to load all employees, not just active ones)
      const status = filters.status || 'all';
      
      // Department filter
      if (filters.department) {
        constraints.push(where('department', '==', filters.department));
      }
      
      // Role filter
      if (filters.role) {
        constraints.push(where('role', '==', filters.role));
      }
      
      // Apply constraints (without ordering for now to avoid issues)
      if (constraints.length > 0) {
        employeesQuery = query(employeesQuery, ...constraints);
      }
      
      const employeesSnap = await getDocs(employeesQuery);
      const employees = [];
      
      employeesSnap.forEach(doc => {
        const data = doc.data();
        const employeeStatus = data.status || 'active';
        
        // Filter by status if needed
        if (status === 'all' || employeeStatus === status) {
          // Spread first so a stored `id` field in document data cannot overwrite Firestore doc id
          employees.push({
            ...data,
            id: doc.id,
            status: employeeStatus
          });
        }
      });
      
      // Sort in JavaScript to handle missing created_at fields
      employees.sort((a, b) => {
        const aDate = a.created_at?.toDate?.() || new Date(0);
        const bDate = b.created_at?.toDate?.() || new Date(0);
        return bDate - aDate;
      });
      
      return { success: true, data: employees };
    } catch (error) {
      console.error('❌ Error loading employees:', error);
      handleFirebaseError(error, 'Company employees retrieval');
      return { success: false, error: error.message };
    }
  }

  // Advanced permission checking
  static async checkEmployeePermissions(companyId, employeeId, requiredPage, requiredOperation = 'view') {
    try {
      const employee = await this.getEmployee(companyId, employeeId);
      if (!employee.success) return { hasPermission: false, reason: 'Employee not found' };
      
      const employeeData = employee.data;
      
      // Owner has all permissions
      if (employeeData.role === 'owner') {
        return { hasPermission: true, reason: 'Owner role' };
      }
      
      // Check if page is assigned
      if (!employeeData.assignedPages?.includes(requiredPage)) {
        return { hasPermission: false, reason: 'Page not assigned' };
      }
      
      // Check specific operation permission
      const permissions = employeeData.permissions || {};
      const pagePermissions = this.getPagePermissions(permissions, requiredPage);
      
      if (!pagePermissions || !pagePermissions[requiredOperation]) {
        return { hasPermission: false, reason: `Operation '${requiredOperation}' not permitted` };
      }
      
      return { hasPermission: true, reason: 'Permission granted' };
    } catch (error) {
      handleFirebaseError(error, 'Permission check');
      return { hasPermission: false, reason: 'Error checking permissions' };
    }
  }

  // Get page permissions from complex permission structure
  static getPagePermissions(permissions, pagePath) {
    const pathParts = pagePath.split('_');
    let current = permissions;
    
    for (const part of pathParts) {
      if (current && current[part]) {
        current = current[part];
      } else {
        return null;
      }
    }
    
    return current;
  }

  // Check if MPIN is required for specific operation
  static checkMPINRequirement(employeeData, pageOperation) {
    return employeeData.mpinRequired?.includes(pageOperation) || false;
  }

  // Get employee by various identifiers
  static async getEmployeeByIdentifier(companyId, identifier, type = 'employeeId') {
    try {
      let employeesQuery;
      
      switch (type) {
        case 'employeeId':
          employeesQuery = query(
            collection(db, `companies/${companyId}/employees`),
            where('employeeId', '==', identifier),
            where('status', '==', 'active')
          );
          break;
        case 'globalEmployeeId':
          employeesQuery = query(
            collection(db, `companies/${companyId}/employees`),
            where('globalEmployeeId', '==', identifier),
            where('status', '==', 'active')
          );
          break;
        case 'email':
          employeesQuery = query(
            collection(db, `companies/${companyId}/employees`),
            where('email', '==', identifier),
            where('status', '==', 'active')
          );
          break;
        case 'phone':
          employeesQuery = query(
            collection(db, `companies/${companyId}/employees`),
            where('phone', '==', identifier),
            where('status', '==', 'active')
          );
          break;
        default:
          return { success: false, error: 'Invalid identifier type' };
      }
      
      const employeesSnap = await getDocs(employeesQuery);
      
      if (employeesSnap.empty) {
        return { success: false, error: 'Employee not found' };
      }
      
      const employeeDoc = employeesSnap.docs[0];
      return { success: true, data: { id: employeeDoc.id, ...employeeDoc.data() } };
    } catch (error) {
      handleFirebaseError(error, 'Employee search');
      return { success: false, error: error.message };
    }
  }

  // Log employee activities
  static async logEmployeeActivity(companyId, employeeId, action, details) {
    try {
      await addDoc(collection(db, `companies/${companyId}/employee_logs`), {
        employeeId,
        action,
        details,
        timestamp: serverTimestamp(),
        performed_by: auth.currentUser?.uid || 'system'
      });
    } catch (error) {
      console.error('Error logging employee activity:', error);
      // Don't throw error for logging failures
    }
  }

  // Migrate employee data from old structure (for KYC migration)
  static async migrateEmployeeFromExpenses(companyId, oldEmployeeData) {
    try {
      const n = (v) => parseFloat(v) || 0;
      const basicAnnual = n(oldEmployeeData.basicSalary);
      const rolledBonus =
        n(oldEmployeeData.hra) +
        n(oldEmployeeData.conveyanceAllowance) +
        n(oldEmployeeData.employerPF) +
        n(oldEmployeeData.gratuity) +
        n(oldEmployeeData.specialAllowance);

      const migratedData = {
        // Basic info
        name: oldEmployeeData.name || '',
        designation: oldEmployeeData.designation || '',
        department: oldEmployeeData.department || 'Other',
        
        // Contact info
        phone: oldEmployeeData.contact || oldEmployeeData.phone || '',
        email: oldEmployeeData.email || '',
        
        // KYC data
        aadhaar: oldEmployeeData.aadhaar || '',
        pan: oldEmployeeData.pan || '',
        bankName: oldEmployeeData.bankName || '',
        bankAccount: oldEmployeeData.bankAccount || '',
        ifsc: oldEmployeeData.ifsc || '',
        address: oldEmployeeData.address || '',
        
        // Salary: basic + bonus (legacy components rolled into bonus)
        basicSalary: basicAnnual,
        bonus: rolledBonus,
        ctc: basicAnnual + rolledBonus,
        
        // System fields
        role: 'employee',
        status: 'active',
        employeeId: oldEmployeeData.employeeId || '',
        
        // Migration metadata
        migrated_from: 'expenses',
        migrated_at: serverTimestamp()
      };

      return await this.createEmployee(companyId, migratedData);
    } catch (error) {
      handleFirebaseError(error, 'Employee migration');
      return { success: false, error: error.message };
    }
  }

  // Auto-migrate old employees to new structure
  static async autoMigrateOldEmployees(companyId) {
    try {
      
      // Check if we have employees in new structure
      const newEmployees = await this.getCompanyEmployees(companyId);
      
      // Check for old employees in the old collection structure (try both paths)
      let oldEmployeesSnap;
      
      try {
        // Try new structure first
        const oldEmployeesRef1 = collection(db, `companies/${companyId}/employees`);
        oldEmployeesSnap = await getDocs(oldEmployeesRef1);
        console.log(`📋 Found ${oldEmployeesSnap.size} employees in companies structure`);
      } catch (error) {
        console.log('No employees in companies structure, trying fallback...');
        try {
          // Fallback to old structure
          const oldEmployeesRef2 = collection(db, `companies/${companyId}/employees`);
          oldEmployeesSnap = await getDocs(oldEmployeesRef2);
          console.log(`📋 Found ${oldEmployeesSnap.size} employees in companies structure`);
        } catch (error2) {
          console.log('No employees found in either structure');
          return { success: true, migratedCount: 0, migratedEmployees: [] };
        }
      }
      
      const migratedEmployees = [];
      
      if (!oldEmployeesSnap.empty) {
        
        for (const employeeDoc of oldEmployeesSnap.docs) {
          const oldData = { id: employeeDoc.id, ...employeeDoc.data() };
          
          // Check if already migrated
          const existingEmployee = newEmployees.data?.find(emp => 
            emp.employeeId === oldData.employeeId || 
            emp.name === oldData.name
          );
          
          if (!existingEmployee) {
            console.log(`🔄 Migrating employee: ${oldData.name} (${oldData.employeeId})`);
            const result = await this.migrateEmployeeFromExpenses(companyId, oldData);
            
            if (result.success) {
              migratedEmployees.push(result);
              console.log(`✅ Migrated: ${oldData.name}`);
            } else {
              console.error(`❌ Failed to migrate ${oldData.name}:`, result.error);
            }
          } else {
            console.log(`⏭️ Employee ${oldData.name} already exists in new structure`);
          }
        }
      }
      
      return { 
        success: true, 
        migratedCount: migratedEmployees.length, 
        migratedEmployees 
      };
    } catch (error) {
      console.error('❌ Auto-migration failed:', error);
      return { success: false, error: error.message };
    }
  }
}

// 🏢 Company Management Utilities
export class CompanyManager {
  static generateCompanyId() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  // Check if user has access to a company
  static checkUserCompanyAccess(userCompanies, companyId) {
    try {
      // Check in owned companies
      const ownedAccess = userCompanies.owned_companies?.some(c => c.companyId === companyId);
      if (ownedAccess) return true;
      
      // Check in employed companies
      const employedAccess = userCompanies.employed_companies?.some(c => c.companyId === companyId);
      if (employedAccess) return true;
      
      // Check in new unified structure
      const unifiedAccess = userCompanies.companies?.some(c => c.companyId === companyId);
      if (unifiedAccess) return true;
      
      return false;
    } catch (error) {
      console.error('Error checking user company access:', error);
      return false;
    }
  }

  static async ensureUserCompaniesDoc(userId) {
    const ref = doc(db, `user_companies/${userId}`);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        // New unified structure
        companies: [],
        active_company: null,
        last_updated: serverTimestamp(),
        // Legacy compatibility
        owned_companies: [],
        employed_companies: []
      });
    }
    return ref;
  }
  
  // Get user companies with new structure support
  static async getUserCompanies(userId) {
    try {
      const userCompaniesRef = doc(db, 'user_companies', userId);
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      
      if (userCompaniesSnap.exists()) {
        const userData = userCompaniesSnap.data();
        
        // Prioritize legacy structure for owner-employee relationship importance
        if (userData.owned_companies && Array.isArray(userData.owned_companies)) {
          // Use legacy structure as primary
          const legacyOwnedCompanies = userData.owned_companies || [];
          const legacyEmployedCompanies = userData.employed_companies || [];
          
          return { 
            success: true, 
            data: {
              // Legacy format (primary)
              owned_companies: legacyOwnedCompanies,
              employed_companies: legacyEmployedCompanies,
              // New structure (secondary, for future compatibility)
              companies: userData.companies || [],
              active_company: userData.active_company,
              last_updated: userData.last_updated,
              // Merge other userData without overriding above
              ...(userData.user_info || {})
            }
          };
        } else if (userData.companies && Array.isArray(userData.companies)) {
          // Convert new structure to format expected by EnhancedCompanyContext
          const ownedCompanies = userData.companies
            .filter(c => c.role === 'owner')
            .map(c => ({
              companyId: c.companyId,
              role: c.role,
              status: c.status || 'active',
              addedAt: c.joinedAt || c.addedAt
            }));
            
          const employedCompanies = userData.companies
            .filter(c => c.role !== 'owner')
            .map(c => ({
              companyId: c.companyId,
              role: c.role,
              status: c.status || 'active',
              addedAt: c.joinedAt || c.addedAt
            }));
          
          return {
            success: true,
            data: {
              // Legacy format for compatibility
              owned_companies: ownedCompanies,
              employed_companies: employedCompanies,
              // New structure
              companies: userData.companies,
              active_company: userData.active_company,
              last_updated: userData.last_updated,
              // Merge other userData without overriding above
              ...(userData.user_info || {})
            } 
          };
        } else {
          return { 
            success: true, 
            data: {
              owned_companies: [], 
              employed_companies: [],
              companies: []
            } 
          };
        }
      } else {
        return {
          success: true, 
          data: { 
            owned_companies: [], 
            employed_companies: [],
            companies: []
          } 
        };
      }
    } catch (error) {
      console.error('❌ Error getting user companies:', error);
      return { success: false, error: error.message };
    }
  }

  static async createCompany(ownerUserId, companyInfo, security = {}) {
    try {
      // Ensure user companies doc exists
      await this.ensureUserCompaniesDoc(ownerUserId);

      // Generate unique company id (retry a few times)
      let companyId = this.generateCompanyId();
      let attempts = 0;
      while (attempts < 5) {
        const check = await getDoc(doc(db, 'companies', companyId));
        if (!check.exists()) break;
        companyId = this.generateCompanyId();
        attempts++;
      }

      // Create unified company root at companies/{companyId}
      const companyName = companyInfo.name || companyInfo.firmName || 'My Company';
      
      await setDoc(doc(db, 'companies', companyId), {
        // System fields (keep at root level for easy access)
        companyId: companyId,
        name: companyName,
        
        // Basic company info organized in map
        basicInfo: {
          businessType: companyInfo.businessType || '',
          companyType: companyInfo.type || '',
          contactPerson: companyInfo.contactPerson || '',
          email: companyInfo.email || '',
          phone: companyInfo.phone || '',
          pan: companyInfo.pan || '',
          gstNumber: companyInfo.gstNumber || '',
          gstType: companyInfo.gstType || '',
          currency: companyInfo.currency || 'INR'
        },
        
        // Owner information
        owner: { 
          userId: ownerUserId, 
          role: 'owner',
          plan: companyInfo.selectedPlan || 'pro',  // Start with PRO trial
          status: 'active',
          addedAt: serverTimestamp()
        },
        
        // System fields
        status: 'active',
        plan: companyInfo.selectedPlan || 'pro',  // Start with PRO trial
        features: { backup_restore: true },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Categorized data using maps
        details: {
          address: companyInfo.address || '',
          city: companyInfo.city || '',
          state: companyInfo.state || '',
          pincode: companyInfo.pincode || '',
          country: companyInfo.country || 'India'
        },
        
        bankDetails: {
          bankName: companyInfo.bankName || '',
          bankAccount: companyInfo.bankAccount || '',
          bankIfsc: companyInfo.bankIfsc || '',
          upiId: companyInfo.upiId || '',
          upiQrUrl: companyInfo.upiQrUrl || ''
        },
        
        branding: {
          logoUrl: companyInfo.logoUrl || '',
          sealUrl: companyInfo.sealUrl || '',
          signatureUrl: companyInfo.signUrl || ''
        },
        
        termsAndConditions: {
          footer: companyInfo.footer || '',
          salesTerms: companyInfo.salesTerms || '',
          purchaseTerms: companyInfo.purchaseTerms || '',
          quotationTerms: companyInfo.quotationTerms || ''
        },
        
        preferences: {
          defaultCurrency: companyInfo.currency || 'INR',
          gstCalculation: companyInfo.gstCalculation || 'excluding',
          dateFormat: companyInfo.dateFormat || 'DD/MM/YYYY',
          selectedPlan: companyInfo.selectedPlan || 'pro'  // Start with PRO trial
        }
      });

      // Use batch write to ensure atomicity and avoid timing issues with security rules
      // This ensures all subcollection writes happen together after company document is committed
      const batch = writeBatch(db);

      // Add owner as employee with all permissions (use 'team_members' to avoid conflict with salary records)
      const teamMemberRef = doc(db, `companies/${companyId}/team_members/${ownerUserId}`);
      batch.set(teamMemberRef, {
        user_id: ownerUserId,
        role: 'owner',
        permissions: ['all'],
        status: 'active',
        hired_date: serverTimestamp(),
        created_at: serverTimestamp()
      });

      // Security: company MPIN + policy (optional)
      const normalizedMpin = (security.companyMpin || '').toString().trim().toUpperCase();
      const policy = {
        requireSwitch: !!security.mpinPolicyRequireSwitch,
        requireSensitive: !!security.mpinPolicyRequireSensitive
      };
      
      if (/^[A-Z0-9]{4}$/.test(normalizedMpin)) {
        const hashed = btoa(normalizedMpin);
        const securityRef = doc(db, `companies/${companyId}/security/company_mpin`);
        batch.set(securityRef, {
          hash: hashed,
          kdf: 'b64',
          policy,
          updated_at: serverTimestamp()
        });
      } else if (policy.requireSwitch || policy.requireSensitive) {
        // Even if MPIN not set, persist policy if toggled
        const securityRef = doc(db, `companies/${companyId}/security/company_mpin`);
        batch.set(securityRef, {
          hash: null,
          kdf: 'b64',
          policy,
          updated_at: serverTimestamp()
        });
      }

      // Commit batch write (all subcollections written atomically)
      await batch.commit();

      // Update user_companies doc with both new and legacy structure
      const userCompaniesRef = doc(db, `user_companies/${ownerUserId}`);
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      const existingData = userCompaniesSnap.exists() ? userCompaniesSnap.data() : {};
      
      // Create new company relationship with all required fields
      const newCompanyRelation = {
        companyId,
        role: 'owner',
        status: 'active',
        joinedAt: new Date(),
        addedVia: 'owner_created',
        updatedAt: new Date(),
        name: companyName,
        type: companyInfo.type || '',
        businessType: companyInfo.businessType || ''
      };
      
      // Prepare user companies data with proper structure
      const userCompaniesData = {
        // Legacy structure
        owned_companies: [...(existingData.owned_companies || []), {
          companyId,
          name: companyName,
          type: companyInfo.type || '',
          businessType: companyInfo.businessType || '',
          role: 'owner',
          created_at: new Date().toISOString(),
          status: 'active'
        }],
        employed_companies: existingData.employed_companies || [],
        
        // New structure
        companies: [...(existingData.companies || []), newCompanyRelation],
        
        // Active company
        active_company: {
          companyId,
          name: companyName,
          type: 'owned',
          switched_at: new Date(),
          mpin_verified: false
        },
        
        // Metadata
        last_updated: new Date()
      };
      
      // Ensure no undefined fields by filtering them out
      const cleanUserCompaniesData = {};
      Object.keys(userCompaniesData).forEach(key => {
        const value = userCompaniesData[key];
        if (value !== undefined) {
          cleanUserCompaniesData[key] = value;
        }
      });
      
      await setDoc(userCompaniesRef, cleanUserCompaniesData, { merge: true });

      // 🎁 Create 1-month PRO trial subscription for new company
      try {
        // Dynamically import to avoid circular dependencies
        const { createTrialSubscription } = await import('./trialSubscriptionUtils.js');
        const trialResult = await createTrialSubscription(db, companyId, ownerUserId);
        
        if (!trialResult.success) {
          console.error('Failed to create trial subscription:', trialResult.error);
          // Continue anyway - company is created
        }
      } catch (error) {
        console.error('Exception during trial subscription creation:', error);
        // Continue anyway - company is created
      }

      return { success: true, companyId };
    } catch (error) {
      handleFirebaseError(error, 'Company creation');
      return { success: false, error: error.message };
    }
  }

  static async ensureCompanyForUser(ownerUserId, companyInfo) {
    try {
      const userCompaniesRef = doc(db, `user_companies/${ownerUserId}`);
      const snap = await getDoc(userCompaniesRef);
      const data = snap.exists() ? snap.data() : null;
      const active = data?.active_company?.companyId || null;
      const owned = (data?.owned_companies || [])[0]?.companyId || null;
      if (active || owned) {
        return { success: true, companyId: active || owned };
      }
      return await this.createCompany(ownerUserId, companyInfo);
    } catch (error) {
      handleFirebaseError(error, 'Ensure company for user');
      return { success: false, error: error.message };
    }
  }
  static async getCompanyInfo(companyId) {
    try {
      console.log('📋 CompanyManager: Getting company info for:', companyId);
      
      // Read from companies/{companyId} for company details
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await getDoc(companyRef);
      
      if (companySnap.exists()) {
        const companyData = companySnap.data();
        console.log('📋 Raw company data from companies collection:', companyData);
        
        // Extract data from new organized structure
        const companyName = companyData.name || `Company ${companyId}`;
        const companyType = companyData.basicInfo?.companyType || '';
        const businessType = companyData.basicInfo?.businessType || '';
        const contactPerson = companyData.basicInfo?.contactPerson || '';
        const email = companyData.basicInfo?.email || '';
        const phone = companyData.basicInfo?.phone || '';
        const pan = companyData.basicInfo?.pan || '';
        const gstNumber = companyData.basicInfo?.gstNumber || '';
        const gstType = companyData.basicInfo?.gstType || '';
        const currency = companyData.basicInfo?.currency || 'INR';
        
        // Address details
        const address = companyData.details?.address || '';
        const city = companyData.details?.city || '';
        const state = companyData.details?.state || '';
        const pincode = companyData.details?.pincode || '';
        const country = companyData.details?.country || 'India';
        
        // Banking details
        const bankName = companyData.bankDetails?.bankName || '';
        const bankAccount = companyData.bankDetails?.bankAccount || '';
        const bankIfsc = companyData.bankDetails?.bankIfsc || '';
        const upiId = companyData.bankDetails?.upiId || '';
        const upiQrUrl = companyData.bankDetails?.upiQrUrl || '';
        
        // Branding
        const logoUrl = companyData.branding?.logoUrl || '';
        const sealUrl = companyData.branding?.sealUrl || '';
        const signatureUrl = companyData.branding?.signatureUrl || '';
        
        // Terms and conditions
        const footer = companyData.termsAndConditions?.footer || '';
        const salesTerms = companyData.termsAndConditions?.salesTerms || '';
        const purchaseTerms = companyData.termsAndConditions?.purchaseTerms || '';
        const quotationTerms = companyData.termsAndConditions?.quotationTerms || '';
        
        // Preferences
        const defaultCurrency = companyData.preferences?.defaultCurrency || currency;
        const gstCalculation = companyData.preferences?.gstCalculation || 'excluding';
        const selectedPlan = companyData.preferences?.selectedPlan || 'basic';
        
        const normalizedData = {
          // System fields
          companyId: companyData.companyId || companyId,
          name: companyName,
          status: companyData.status || 'active',
          plan: companyData.plan || 'basic',
          features: companyData.features || {},
          owner: companyData.owner || {},
          createdAt: companyData.createdAt,
          updatedAt: companyData.updatedAt,
          
          // Basic info (for backward compatibility)
          companyType: companyType,
          businessType: businessType,
          contactPerson: contactPerson,
          email: email,
          phone: phone,
          pan: pan,
          gstNumber: gstNumber,
          gstType: gstType,
          currency: currency,
          
          // Address info
          address: address,
          city: city,
          state: state,
          pincode: pincode,
          country: country,
          
          // Banking info
          bankName: bankName,
          bankAccount: bankAccount,
          bankIfsc: bankIfsc,
          upiId: upiId,
          upiQrUrl: upiQrUrl,
          
          // Branding
          logoUrl: logoUrl,
          sealUrl: sealUrl,
          signatureUrl: signatureUrl,
          signUrl: signatureUrl,
          branding: {
            logoUrl,
            sealUrl,
            signatureUrl,
          },
          
          // Terms
          footer: footer,
          salesTerms: salesTerms,
          purchaseTerms: purchaseTerms,
          quotationTerms: quotationTerms,
          
          // Preferences
          defaultCurrency: defaultCurrency,
          gstCalculation: gstCalculation,
          selectedPlan: selectedPlan,
          
          // Details object for backward compatibility
          details: {
            firmName: companyName,
            name: companyName,
            companyType: companyType,
            businessType: businessType,
            contactPerson: contactPerson,
            email: email,
            phone: phone,
            pan: pan,
            gstNumber: gstNumber,
            gstType: gstType,
            currency: currency,
            address: address,
            city: city,
            state: state,
            pincode: pincode,
            country: country,
            bankName: bankName,
            bankAccount: bankAccount,
            bankIfsc: bankIfsc,
            upiId: upiId,
            upiQrUrl: upiQrUrl,
            logoUrl: logoUrl,
            sealUrl: sealUrl,
            signatureUrl: signatureUrl,
            footer: footer,
            salesTerms: salesTerms,
            purchaseTerms: purchaseTerms,
            quotationTerms: quotationTerms
          }
        };
        
        console.log('📋 Normalized company data:', normalizedData);
        return { success: true, data: normalizedData };
      } else {
        console.log('❌ Company not found in companies collection:', companyId);
        return { success: false, error: 'Company not found' };
      }
    } catch (error) {
      console.error('❌ Error getting company info:', error);
      handleFirebaseError(error, 'Company info retrieval');
      return { success: false, error: error.message };
    }
  }
  
  static async updateCompanyInfo(companyId, updateData) {
    try {
      // Update unified companies/{companyId} root
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'Company info update');
      return { success: false, error: error.message };
    }
  }
  
  static async getUserCompanies(userId) {
    try {
      const userCompaniesRef = doc(db, `user_companies/${userId}`);
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      
      if (userCompaniesSnap.exists()) {
        const userCompaniesData = userCompaniesSnap.data();
        
        // Fetch company details for each company
        const companiesWithDetails = [];
        
        // Process owned companies
        if (userCompaniesData.companies && Array.isArray(userCompaniesData.companies)) {
          for (const companyRef of userCompaniesData.companies) {
            try {
              const companyDetails = await this.getCompanyInfo(companyRef.companyId);
              if (companyDetails.success) {
                companiesWithDetails.push({
                  ...companyRef,
                  details: companyDetails.data
                });
              } else {
                // Fallback if company details not found
                companiesWithDetails.push(companyRef);
              }
            } catch (error) {
              console.error('Error fetching company details for:', companyRef.companyId, error);
              companiesWithDetails.push(companyRef);
            }
          }
        }
        
        return { 
          success: true, 
          data: {
            ...userCompaniesData,
            companies: companiesWithDetails
          }
        };
      } else {
        return { success: false, error: 'User companies not found' };
      }
    } catch (error) {
      handleFirebaseError(error, 'User companies retrieval');
      return { success: false, error: error.message };
    }
  }
  
  static async switchCompany(userId, companyId, type = 'owned') {
    try {
      const userCompaniesRef = doc(db, `user_companies/${userId}`);
      await updateDoc(userCompaniesRef, {
        active_company: {
          companyId,
          type,
          switched_at: serverTimestamp(),
          mpin_verified: false
        },
        last_updated: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'Company switching');
      return { success: false, error: error.message };
    }
  }
}

// 📊 Session Management Utilities
export class SessionManager {
  static async createUserSession(userId, companyId) {
    try {
      const sessionRef = await addDoc(collection(db, 'user_sessions'), {
        user_id: userId,
        company_id: companyId,
        login_time: serverTimestamp(),
        last_activity: serverTimestamp(),
        ip_address: 'client_ip', // Get from request
        user_agent: navigator.userAgent,
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
        active: true
      });
      
      return { success: true, sessionId: sessionRef.id };
    } catch (error) {
      handleFirebaseError(error, 'Session creation');
      return { success: false, error: error.message };
    }
  }
  
  static async updateSessionActivity(sessionId) {
    try {
      const sessionRef = doc(db, 'user_sessions', sessionId);
      await updateDoc(sessionRef, {
        last_activity: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'Session activity update');
      return { success: false, error: error.message };
    }
  }
  
  static async endSession(sessionId) {
    try {
      const sessionRef = doc(db, 'user_sessions', sessionId);
      await updateDoc(sessionRef, {
        active: false,
        last_activity: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'Session ending');
      return { success: false, error: error.message };
    }
  }
}

// 📁 File Management Utilities
export class FileManager {
  static async uploadFile(file, path, metadata = {}) {
    try {
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return { success: true, url: downloadURL, path: snapshot.ref.fullPath };
    } catch (error) {
      handleFirebaseError(error, 'File upload');
      return { success: false, error: error.message };
    }
  }
  
  static async deleteFile(path) {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'File deletion');
      return { success: false, error: error.message };
    }
  }
  
  static async getFileURL(path) {
    try {
      const storageRef = ref(storage, path);
      const url = await getDownloadURL(storageRef);
      
      return { success: true, url };
    } catch (error) {
      handleFirebaseError(error, 'File URL retrieval');
      return { success: false, error: error.message };
    }
  }
}

// 🔍 Audit Logging Utilities
export class AuditLogger {
  static async logAction(userId, companyId, action, details = {}) {
    try {
      await addDoc(collection(db, 'access_logs'), {
        user_id: userId,
        company_id: companyId,
        action,
        data_access: details.dataAccess || 'unknown',
        timestamp: serverTimestamp(),
        ip_address: 'client_ip', // Get from request
        user_agent: navigator.userAgent,
        success: true,
        error_message: null,
        ...details
      });
      
      return { success: true };
    } catch (error) {
      handleFirebaseError(error, 'Audit logging');
      return { success: false, error: error.message };
    }
  }
  
  static async logError(userId, companyId, action, errorMessage, details = {}) {
    try {
      await addDoc(collection(db, 'access_logs'), {
        user_id: userId,
        company_id: companyId,
        action,
        data_access: details.dataAccess || 'unknown',
        timestamp: serverTimestamp(),
        ip_address: 'client_ip',
        user_agent: navigator.userAgent,
        success: false,
        error_message: errorMessage,
        ...details
      });
      
      return { success: true };
    } catch (error) {
      console.error('Failed to log error:', error);
      return { success: false, error: error.message };
    }
  }
}

// 👤 User Profile Management for UID-based Employee Addition
export class UserProfileManager {
  
  // Fetch user profile by UID for employee addition
  static async getUserProfileByUID(userId) {
    try {
      console.log('🔍 Fetching user profile for UID:', userId);
      
      // Try to get user profile from 'users' collection
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        console.log('✅ Found user profile:', userData);
        
        return {
          success: true,
          profile: {
            uid: userId,
            email: userData.email || '',
            displayName: userData.displayName || userData.name || '',
            phoneNumber: userData.phoneNumber || userData.phone || '',
            emailVerified: userData.emailVerified || false,
            role: userData.role || 'user',
            status: userData.status || 'active',
            createdAt: userData.createdAt,
            lastLoginAt: userData.lastLoginAt,
            // Additional profile data
            profileComplete: !!(userData.displayName && userData.email)
          }
        };
      } else {
        console.log('❌ User profile not found in users collection');
        return { 
          success: false, 
          error: 'User profile not found. User may not be registered yet.' 
        };
      }
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
      
      // Handle specific permission errors
      if (error.code === 'permission-denied') {
        return { 
          success: false, 
          error: 'Permission denied. Please ensure you have proper access rights to view user profiles. This may be due to Firestore security rules - please contact system administrator.' 
        };
      }
      
      return { 
        success: false, 
        error: `Failed to fetch user profile: ${error.message}` 
      };
    }
  }
  
  // Check if user is already employee in any company
  static async checkUserEmploymentStatus(userId, currentCompanyId = null) {
    try {
      console.log('🔍 Checking employment status for UID:', userId);
      
      // First check if already employee in current company
      if (currentCompanyId) {
        const existingEmployees = await EmployeeManager.getCompanyEmployees(currentCompanyId);
        if (existingEmployees.success) {
          const duplicateEmployee = existingEmployees.data.find(emp => emp.userId === userId);
          if (duplicateEmployee) {
            return {
              success: true,
              isEmployed: true,
              isDuplicateInCurrentCompany: true,
              duplicateEmployee: duplicateEmployee,
              companies: [],
              totalCompanies: 0,
              error: `User is already an employee in this company with Employee ID: ${duplicateEmployee.employeeId}`
            };
          }
        }
      }
      
      // Check in user_companies document for other companies
      const userCompaniesRef = doc(db, 'user_companies', userId);
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      
      if (userCompaniesSnap.exists()) {
        const userData = userCompaniesSnap.data();
        const companies = userData.companies || [];
        
        console.log('📋 Found user companies:', companies);
        
        return {
          success: true,
          isEmployed: companies.length > 0,
          isDuplicateInCurrentCompany: false,
          companies: companies.map(company => ({
            companyId: company.companyId,
            role: company.role,
            status: company.status,
            joinedAt: company.joinedAt
          })),
          totalCompanies: companies.length
        };
      } else {
        console.log('📋 No company associations found');
        return {
          success: true,
          isEmployed: false,
          isDuplicateInCurrentCompany: false,
          companies: [],
          totalCompanies: 0
        };
      }
    } catch (error) {
      console.error('❌ Error checking employment status:', error);
      return { 
        success: false, 
        error: `Failed to check employment status: ${error.message}` 
      };
    }
  }
  
  // Create new employee account with owner-set password
  static async createEmployeeAccount(employeeData, companyId, initialPassword) {
    try {
      console.log('🔄 Creating new employee account:', { email: employeeData.email, companyId });
      
      // Create Firebase Auth account with owner-set password
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        employeeData.email, 
        initialPassword
      );
      
      const user = userCredential.user;
      console.log('✅ Firebase Auth account created:', user.uid);
      
      // Update user profile with display name
      if (employeeData.name) {
        await updateProfile(user, {
          displayName: employeeData.name
        });
      }
      
      // Send email verification
      await sendEmailVerification(user, {
        url: `${window.location.origin}/#/login`,
        handleCodeInApp: false,
      });
      console.log('📧 Email verification sent');
      
      // Generate employee IDs
      const employeeId = await EmployeeManager.generateEmployeeId(companyId);
      const globalEmployeeId = await EmployeeManager.generateGlobalEmployeeId();
      
      // Create employee record
      const employeeRecord = {
        // Link to user account
        userId: user.uid,
        
        // Employee identifiers
        employeeId: employeeId,
        globalEmployeeId: globalEmployeeId,
        
        // Basic info
        name: employeeData.name || '',
        email: employeeData.email || '',
        phone: employeeData.phone || '',
        
        // Employee-specific details
        designation: employeeData.designation || 'Employee',
        department: employeeData.department || 'General',
        role: employeeData.role || 'employee',
        
        // Employment details
        joiningDate: employeeData.joiningDate || new Date(),
        employmentType: employeeData.employmentType || 'full_time',
        
        // Account status
        status: 'pending_verification', // Will change to 'active' after email verification
        inviteStatus: 'account_created', // Track that account was created by owner
        addedVia: 'owner_created', // Track creation method
        
        // Password management
        passwordSetBy: 'owner',
        requiresPasswordChange: true, // Force password change after verification
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        accountCreatedAt: serverTimestamp(),
        
        // Additional data
        ...employeeData
      };
      
      console.log('📝 Creating employee record:', employeeRecord);
      
      // Add employee to company
      const employeeRef = doc(db, `companies/${companyId}/employees/${employeeId}`);
      await setDoc(employeeRef, employeeRecord);
      
      // Create user profile
      await this.createUserProfile(user.uid, {
        email: user.email,
        displayName: employeeData.name || '',
        phoneNumber: employeeData.phone || '',
        role: 'employee',
        status: 'pending_verification',
        emailVerified: false,
        accountCreatedBy: 'owner',
        requiresPasswordChange: true
      });
      
      // Create user-company relationship
      console.log('🔗 About to create user-company relationship for new account...');
      const relationResult = await this.createUserCompanyRelation(user.uid, companyId, employeeData.role || 'employee', 'owner_created');
      console.log('🔗 User-company relation result for new account:', relationResult);
      
      if (!relationResult.success) {
        console.error('❌ Failed to create user-company relation for new account:', relationResult.error);
        // Don't fail the entire process, but log the error
      }
      
      // Sign out the newly created user (so owner stays logged in)
      await signOut(auth);
      
      console.log('✅ Successfully created employee account');
      
      return {
        success: true,
        userId: user.uid,
        employeeId: employeeId,
        globalEmployeeId: globalEmployeeId,
        employeeRecord: employeeRecord,
        credentials: {
          email: employeeData.email,
          password: initialPassword // Return for owner to share with employee
        }
      };
      
    } catch (error) {
      console.error('❌ Error creating employee account:', error);
      
      // Handle specific Firebase Auth errors
      let errorMessage = error.message;
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists. Use UID-based addition instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address format.';
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }

  // Create user profile for new employee
  static async createUserProfile(userId, userData) {
    try {
      const userRef = doc(db, 'users', userId);
      
      const profileData = {
        uid: userId,
        email: userData.email || '',
        displayName: userData.displayName || '',
        phoneNumber: userData.phoneNumber || '',
        role: userData.role || 'employee',
        status: userData.status || 'active',
        emailVerified: userData.emailVerified || false,
        accountCreatedBy: userData.accountCreatedBy || 'self',
        requiresPasswordChange: userData.requiresPasswordChange || false,
        createdAt: serverTimestamp(),
        lastLoginAt: null,
        updatedAt: serverTimestamp(),
        ...userData
      };
      
      await setDoc(userRef, profileData);
      console.log('✅ User profile created:', userId);
      
      return { success: true, profile: profileData };
      
    } catch (error) {
      console.error('❌ Error creating user profile:', error);
      return { success: false, error: error.message };
    }
  }

  // Add existing user as employee by UID
  static async addExistingUserAsEmployee(userId, companyId, employeeData) {
    try {
      console.log('🔄 Adding existing user as employee:', { userId, companyId, employeeData });
      
      // Fetch user profile to get basic info
      const profileResult = await this.getUserProfileByUID(userId);
      if (!profileResult.success) {
        return { success: false, error: profileResult.error };
      }
      
      const userProfile = profileResult.profile;
      
      // Generate employee ID
      const employeeId = await EmployeeManager.generateEmployeeId(companyId);
      const globalEmployeeId = await EmployeeManager.generateGlobalEmployeeId();
      
      // Prepare employee record with user profile data
      const employeeRecord = {
        // Link to user account
        userId: userId,
        
        // Employee identifiers
        employeeId: employeeId,
        globalEmployeeId: globalEmployeeId,
        
        // Basic info from user profile + provided data
        name: employeeData.name || userProfile.displayName || 'Unknown',
        email: userProfile.email || employeeData.email || '',
        phone: userProfile.phoneNumber || employeeData.phone || '',
        
        // Employee-specific details
        designation: employeeData.designation || 'Employee',
        department: employeeData.department || 'General',
        role: employeeData.role || 'employee',
        
        // Employment details
        joiningDate: employeeData.joiningDate || new Date(),
        employmentType: employeeData.employmentType || 'full_time',
        
        // Status and tracking
        status: 'active',
        inviteStatus: 'joined', // Since user already exists
        addedVia: 'uid_direct', // Track how employee was added
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        joinedAt: serverTimestamp(),
        
        // Additional data
        ...employeeData
      };
      
      console.log('📝 Creating employee record:', employeeRecord);
      
      // Add employee to company
      const employeeRef = doc(db, `companies/${companyId}/employees/${employeeId}`);
      await setDoc(employeeRef, employeeRecord);
      
      // Create user-company relationship  
      console.log('🔗 About to create user-company relationship...');
      const relationResult = await this.createUserCompanyRelation(userId, companyId, employeeData.role || 'employee', 'uid_direct');
      console.log('🔗 User-company relation result:', relationResult);
      
      if (!relationResult.success) {
        console.error('❌ Failed to create user-company relation:', relationResult.error);
        // Don't fail the entire process, but log the error
      }
      
      console.log('✅ Successfully added existing user as employee');
      
      return {
        success: true,
        employeeId: employeeId,
        globalEmployeeId: globalEmployeeId,
        employeeRecord: employeeRecord
      };
      
    } catch (error) {
      console.error('❌ Error adding existing user as employee:', error);
      return { 
        success: false, 
        error: `Failed to add user as employee: ${error.message}` 
      };
    }
  }
  
  // Helper: Create user-company relationship with proper structure
  static async createUserCompanyRelation(userId, companyId, role, addedVia = 'uid_direct') {
    try {
      console.log('🔗 Creating user-company relationship:', { userId, companyId, role, addedVia });
      console.log('📍 Function called from:', new Error().stack);
      
      const userCompaniesRef = doc(db, 'user_companies', userId);
      console.log('📄 Document reference path:', userCompaniesRef.path);
      
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      console.log('📖 Document exists:', userCompaniesSnap.exists());
      
      const existingData = userCompaniesSnap.exists() ? userCompaniesSnap.data() : {};
      const companies = existingData.companies || [];
      
      // Check if relationship already exists
      const existingIndex = companies.findIndex(c => c.companyId === companyId);
      
      // Create timestamp once to avoid serverTimestamp() in arrays
      const now = new Date();
      const companyRelation = {
        companyId,
        role,
        status: 'active',
        joinedAt: now,
        addedVia,
        updatedAt: now
      };
      
      if (existingIndex >= 0) {
        // Update existing relationship
        companies[existingIndex] = {
          ...companies[existingIndex],
          ...companyRelation
        };
        console.log('🔄 Updated existing company relationship');
      } else {
        // Add new relationship
        companies.push(companyRelation);
        console.log('🆕 Added new company relationship');
      }
      
      // Prepare user companies data with proper structure
      const userCompaniesData = {
        companies,
        active_company: {
          companyId,
          type: role === 'owner' ? 'owned' : 'employed',
          switched_at: serverTimestamp()
        },
        last_updated: serverTimestamp()
      };
      
      // Merge with existing data if any
      if (existingData.user_info) {
        userCompaniesData.user_info = existingData.user_info;
      }
      
      // Update user companies document
      console.log('💾 Saving user companies data:', userCompaniesData);
      await setDoc(userCompaniesRef, userCompaniesData, { merge: true });
      
      // Verify the document was saved
      const verifySnap = await getDoc(userCompaniesRef);
      console.log('✅ Document saved successfully:', verifySnap.exists());
      if (verifySnap.exists()) {
        console.log('📋 Saved data:', verifySnap.data());
      }
      
      console.log('✅ User-company relationship created/updated with proper structure');
      
      // Trigger a custom event to notify components about user companies update
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('userCompaniesUpdated', { 
          detail: { userId, companyId, role } 
        }));
      }
      
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error creating user-company relation:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Validate UID format
  static validateUID(uid) {
    if (!uid || typeof uid !== 'string') {
      return { valid: false, error: 'UID is required and must be a string' };
    }
    
    // Firebase UID is typically 28 characters long
    if (uid.length < 20 || uid.length > 40) {
      return { valid: false, error: 'Invalid UID format. Firebase UIDs are typically 28 characters long.' };
    }
    
    // Basic alphanumeric check
    if (!/^[a-zA-Z0-9]+$/.test(uid)) {
      return { valid: false, error: 'UID should only contain alphanumeric characters' };
    }
    
    return { valid: true };
  }
  
  // Test function to manually create user-company relationship
  static async testCreateUserCompanyRelation(userId, companyId, role = 'employee') {
    console.log('🧪 Testing user-company relationship creation...');
    try {
      const result = await this.createUserCompanyRelation(userId, companyId, role, 'manual_test');
      console.log('🧪 Test result:', result);
      return result;
    } catch (error) {
      console.error('🧪 Test failed:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Debug function to test complete employee flow
  static async debugEmployeeFlow(userId, companyId) {
    console.log('🐛 DEBUG: Testing complete employee flow...');
    
    try {
      // 1. Check user profile
      console.log('🐛 Step 1: Checking user profile...');
      const profileResult = await UserProfileManager.getUserProfileByUID(userId);
      console.log('🐛 Profile result:', profileResult);
      
      // 2. Check employment status
      console.log('🐛 Step 2: Checking employment status...');
      const employmentResult = await UserProfileManager.checkUserEmploymentStatus(userId, companyId);
      console.log('🐛 Employment result:', employmentResult);
      
      // 3. Check user_companies document
      console.log('🐛 Step 3: Checking user_companies document...');
      const userCompaniesRef = doc(db, 'user_companies', userId);
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      console.log('🐛 User companies exists:', userCompaniesSnap.exists());
      if (userCompaniesSnap.exists()) {
        console.log('🐛 User companies data:', userCompaniesSnap.data());
      }
      
      // 4. Check company document
      console.log('🐛 Step 4: Checking company document...');
      const companyResult = await CompanyManager.getCompanyInfo(companyId);
      console.log('🐛 Company info result:', companyResult);
      
      // 5. Test CompanyManager.getUserCompanies
      console.log('🐛 Step 5: Testing CompanyManager.getUserCompanies...');
      const userCompaniesResult = await CompanyManager.getUserCompanies(userId);
      console.log('🐛 getUserCompanies result:', userCompaniesResult);
      
      return { success: true, debug: 'Complete' };
      
    } catch (error) {
      console.error('🐛 Debug flow failed:', error);
      return { success: false, error: error.message };
    }
  }
}

// All utilities are already exported as individual classes above

// 🐛 DEBUG: Expose debug functions globally for console testing
if (typeof window !== 'undefined') {
  window.ACCTOO_DEBUG = {
    UserProfileManager,
    CompanyManager,
    EmployeeManager,
    // Fixed debug functions with proper context
    debugEmployeeFlow: (userId, companyId) => UserProfileManager.debugEmployeeFlow(userId, companyId),
    testUserCompanyRelation: (userId, companyId, role) => UserProfileManager.testCreateUserCompanyRelation(userId, companyId, role),
    // Context refresh helpers
    triggerUserCompaniesUpdate: (userId, companyId, role) => {
      window.dispatchEvent(new CustomEvent('userCompaniesUpdated', { 
        detail: { userId, companyId, role } 
      }));
    },
    // Manual page refresh
    refreshPage: () => window.location.reload()
  };
  console.log('🐛 ACCTOO Debug tools loaded. Use: window.ACCTOO_DEBUG');
}
